import { getCurrencySymbol, ViewportScroller } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService, CxNumericPipe } from '@spartacus/core';
import { LAUNCH_CALLER, LaunchDialogService } from '@spartacus/storefront';
import { BehaviorSubject, combineLatest, merge, Observable, of, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ArticlePickerService } from '../../../../core/catalog';
import { TranslationService } from '../../../../core/i18n';
import { ModalCloseReason } from '../../../../core/modal';
import {
  Article,
  ArticlePickerAttribute,
  ArticlePickerEvent,
  ArticlePrice,
  HorizontalAlignment,
  PrePrintedSpecial,
  Product,
  Unit,
} from '../../../../core/model';
import { CurrencyService } from '../../../../core/site-context';
import { OrderCardsFacade, PriceFacade, PrincipalConfigurationService } from '../../../../core/user';
import { shallowEqualArrays, shallowEqualObjects } from '../../../../core/util';
import { UnitPipe } from '../../../../shared/pipes/unit.pipe';
import { ArticleDetails, ArticleDetailsService } from '../../../../shared/services';
import { resolveDefaultArticleQuantity } from '../../../../shared/utils/resolve-default-article-quantity';
import { resolveDefaultArticleUnit } from '../../../../shared/utils/resolve-default-article-unit';
import { ActiveCartFacade } from '../../../cart/base';
import { ItemListType } from '../../../tracking';
import { PdpListVariantsModalData } from '../pdp-list-variants-modal/pdp-list-variants-modal.component';

@Component({
  selector: 'py-pdp-sku-picker',
  templateUrl: './pdp-sku-picker.component.html',
  styleUrls: ['./pdp-sku-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class PdpSkuPickerComponent implements OnInit, OnDestroy {
  protected subscription = new Subscription();

  article$ = new BehaviorSubject<Article | undefined>(undefined);
  product$ = new BehaviorSubject<Product | undefined>(undefined);
  areaOfUseCode$ = new BehaviorSubject<string | undefined>(undefined);
  outOfStockOnWebshop$ = new BehaviorSubject<boolean>(false);
  private activeUnit$ = new BehaviorSubject<Unit>(undefined);

  articles$: Observable<Article[]>;
  articleDetails$: Observable<ArticleDetails>;
  quantityFormValueChanges$: Observable<{ quantity: number; unitCode: string }>;
  quantity$: Observable<number>;
  ecommerceQuantity$: Observable<number>;
  unitCode$: Observable<string>;
  units$: Observable<object[]>;
  attributes$: Observable<ArticlePickerAttribute[]>;
  selectedAttributes$ = new BehaviorSubject<ArticlePickerEvent[]>([]);
  showMinimumOrderError$: Observable<boolean>;
  disableActionButtons$: Observable<boolean>;
  enableSecondaryArticleRowVariant$: Observable<boolean>;
  isUserLoggedIn$: Observable<boolean>;
  activeCurrency$: Observable<string>;
  breakableFeeHint$: Observable<string>;
  articlePrice$: Observable<ArticlePrice>;
  loadingPrice$: Observable<boolean>;
  enableArticleDetailsSection$: Observable<boolean>;
  enableDisplayOfPrices$: Observable<boolean>;
  emitLoadPrice$ = new Subject<void>();

  set activeUnit(activeUnit: Unit) {
    this.activeUnit$.next(activeUnit);
  }
  get activeUnit(): Unit {
    return this.activeUnit$.value;
  }
  quantityStep = 1;
  showError = false;
  invalid: boolean = false;
  showAListOfAllAvailableVariantsLink: boolean;
  horizontalAlignments = HorizontalAlignment;

  prePrintedCountryForm: FormGroup = new FormGroup({
    configuration: new FormControl(''),
  });

  quantityForm: UntypedFormGroup = new UntypedFormGroup({
    quantity: new UntypedFormControl(0),
    unitCode: new UntypedFormControl(''),
  });

  @Input() set areaOfUseCode(areaOfUseCode: string) {
    this.areaOfUseCode$.next(areaOfUseCode);
  }

  @Input()
  set product(product: Product) {
    this.invalid = true;
    this.product$.next(product);
    this.selectedAttributes$.next([]);
    this.showError = false;
    this.showAListOfAllAvailableVariantsLink = product?.articles?.length > 1 && product?.articlePickerAttributes?.length > 0;
  }

  @Input()
  set article(article: Article) {
    this.activeUnit = null;
    this.article$.next(article);
    this.showError = false;
  }

  @Output() setQueryParams = new EventEmitter<{ ks: boolean | null; a: string | null }>();
  @Output() articleOutOfStockChange = new EventEmitter<{ status: boolean; outOfStockOnWebshop: boolean }>();

  get prePrintedConfigurationControl(): FormControl {
    return this.prePrintedCountryForm.get('configuration') as FormControl;
  }

  constructor(
    private articlePickerService: ArticlePickerService,
    private articleDetailsService: ArticleDetailsService,
    private activeCartService: ActiveCartFacade,
    private cxDecimalPipe: CxNumericPipe,
    private unitPipe: UnitPipe,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private viewportScroller: ViewportScroller,
    private orderCardsService: OrderCardsFacade,
    private principalConfigurationService: PrincipalConfigurationService,
    private translationService: TranslationService,
    private authService: AuthService,
    private currencyService: CurrencyService,
    private priceService: PriceFacade,
    private launchDialogService: LaunchDialogService
  ) {}

  ngOnInit(): void {
    this.enableSecondaryArticleRowVariant$ = this.principalConfigurationService
      .isEnabled('enableSecondaryArticleRowVariant')
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.enableArticleDetailsSection$ = this.articleDetailsService.articleDetailsSectionEnabled();

    this.articles$ = combineLatest([this.product$, this.areaOfUseCode$]).pipe(
      filter(([product]) => !!product),
      switchMap(([product, areaOfUseCode]) => this.articlePickerService.getArticles(product, areaOfUseCode))
    );

    this.articleDetails$ = combineLatest([this.article$, this.activeUnit$]).pipe(
      switchMap(([article, activeUnit]) => this.articleDetailsService.getArticleDetails(article, activeUnit?.code))
    );

    this.showMinimumOrderError$ = combineLatest([
      this.quantityForm.valueChanges.pipe(startWith(this.quantityForm.value), distinctUntilChanged(shallowEqualObjects)),
      this.article$,
    ]).pipe(
      filter(([_value, article]) => !!article),
      map(([value, article]) => {
        const unit = article?.units?.find((u) => u.code === value.unitCode);
        return !!unit && unit.inEcommerceUnit * value.quantity < article.minimumOrderQuantity;
      })
    );

    const useArticleUnitAsDefault$ = this.principalConfigurationService
      .isEnabled('useArticleUnitAsDefault')
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.subscription.add(
      combineLatest([this.article$.pipe(filter((article) => !!article)), useArticleUnitAsDefault$]).subscribe(
        ([article, useArticleUnitAsDefault]) => {
          const defaultUnit = resolveDefaultArticleUnit(article, useArticleUnitAsDefault);
          const defaultQuantity = resolveDefaultArticleQuantity(article, defaultUnit, useArticleUnitAsDefault);

          this.quantityForm.get('unitCode').setValue(defaultUnit?.code || '', { emitEvent: false });
          this.quantityForm.get('quantity').setValue(defaultQuantity);

          this.quantityStep = defaultQuantity;
          this.activeUnit = defaultUnit;
        }
      )
    );

    this.subscription.add(
      this.quantityForm
        .get('unitCode')
        .valueChanges.pipe(
          withLatestFrom(this.article$),
          filter(([value, article]) => !!value && !!article && article?.units?.some((u) => u.code === value))
        )
        .subscribe(([value, article]) => {
          const unit = article?.units?.find((u) => u.code === value);
          const newQuantity = Math.ceil(article?.deliveryQuantity / (unit?.inEcommerceUnit || 0));
          this.quantityStep = newQuantity;
          this.quantityForm.get('quantity').setValue(newQuantity);
          this.activeUnit = unit;
        })
    );

    this.quantityFormValueChanges$ = this.quantityForm.valueChanges.pipe(
      startWith(this.quantityForm.value),
      filter((value) => !!value.quantity && !!value.unitCode),
      debounceTime(500),
      distinctUntilChanged((prevVal, currVal) => shallowEqualObjects(prevVal, currVal)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.quantity$ = this.quantityFormValueChanges$.pipe(
      map((value) => value.quantity),
      startWith(0)
    );

    this.unitCode$ = this.quantityFormValueChanges$.pipe(
      map((value) => value.unitCode),
      startWith('')
    );

    const valueChanges$ = combineLatest([this.article$, this.quantity$, this.unitCode$]).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.ecommerceQuantity$ = valueChanges$.pipe(
      filter(([article, quantity, unitCode]) => !!article && !!quantity && !!unitCode),
      switchMap(([article, quantity, unitCode]) => {
        const unit = article?.units?.find((u) => u.code === unitCode);
        return of(quantity * unit?.inEcommerceUnit);
      })
    );

    this.units$ = combineLatest([
      this.quantityForm.get('quantity').valueChanges.pipe(startWith(this.quantityForm.get('quantity').value)),
      this.article$,
    ]).pipe(
      filter(([quantity, article]) => quantity > 0 && !!article),
      map(([quantity, article]) => {
        return article.units.map((unit) => ({
          value: unit.code,
          label: this.unitPipe.transform(unit, quantity),
          description: `${this.cxDecimalPipe.transform(unit?.inEcommerceUnit, '1.0-0')} ${this.unitPipe.transform(
            article.unit,
            unit?.inEcommerceUnit
          )}`,
        }));
      })
    );

    this.subscription.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          map((event) => event as NavigationEnd)
        )
        .subscribe((_event) => {
          const prevScrollPos = this.router.getCurrentNavigation()?.extras?.state?.scrollTop;
          if (prevScrollPos) {
            this.viewportScroller.scrollToPosition([0, prevScrollPos]);
          }
        })
    );

    this.attributes$ = combineLatest([this.product$, this.areaOfUseCode$]).pipe(
      filter(([product]) => !!product),
      switchMap(([product, areaOfUseCode]) => this.articlePickerService.getAttributes(product, areaOfUseCode)),
      tap((_attributes) => {
        this.invalid = false;
      })
    );

    this.subscription.add(
      combineLatest([this.article$, this.attributes$])
        .pipe(filter(([article, attributes]) => !!article && attributes.length > 0))
        .subscribe(([article, attributes]) => {
          const values = [];
          const ks = this.activatedRoute.snapshot.queryParamMap.has('ks');
          attributes.forEach((attribute) => {
            const value = article?.classificationAttributes[attribute.code]?.value || '-';
            values.push({
              attributeCode: attribute.code,
              value,
              auto: ks
                ? this.selectedAttributes$.value.find((sa) => sa.attributeCode === attribute.code)?.auto || false
                : attributes?.length !== 1,
            });
          });

          this.selectedAttributes$.next(values);
        })
    );

    this.disableActionButtons$ = combineLatest([
      this.showMinimumOrderError$.pipe(startWith(false)),
      this.orderCardsService.isOrderCardsUserEnabled(),
      this.article$.pipe(
        map((article) => article?.salesBlocked),
        startWith(false)
      ),
      this.outOfStockOnWebshop$.pipe(startWith(false)),
    ]).pipe(
      map(
        ([showMinimumOrderError, isOrderCardsUserEnabled, salesBlocked, outOfStockOnWebshop]) =>
          showMinimumOrderError || isOrderCardsUserEnabled || salesBlocked || outOfStockOnWebshop
      )
    );

    this.subscription.add(
      this.selectedAttributes$
        .pipe(
          distinctUntilChanged(shallowEqualArrays),
          withLatestFrom(this.articles$),
          filter(([selectedAttributes, articles]) => selectedAttributes?.length > 0 && articles.length > 0),
          switchMap(([selectedAttributes, articles]) =>
            of(this.articlePickerService.getFilteredArticles(articles, selectedAttributes))
          ),
          debounceTime(10),
          withLatestFrom(this.attributes$),
          filter(
            ([filteredArticles, attributes]) =>
              filteredArticles?.length === 1 || this.selectedAttributes$.value?.length === attributes.length
          ),
          map(([filteredArticles, _attributes]) => filteredArticles)
        )
        .subscribe((filteredArticles) => {
          const activeArticleCode =
            this.activatedRoute.snapshot.queryParamMap.has('a') && this.activatedRoute.snapshot.queryParamMap.get('a');
          const activeArticle = activeArticleCode && filteredArticles.find((a) => a.code === activeArticleCode);

          if (activeArticle) {
            return;
          }

          if (filteredArticles.length === 1) {
            this.setQueryParams.emit({
              ks: true,
              a: filteredArticles.length === 1 ? filteredArticles[0]?.code : activeArticleCode,
            });
          } else if (filteredArticles.length > 1) {
            this.openModal(true);

            this.subscription.add(
              this.launchDialogService.dialogClose
                .pipe(
                  filter((reason) => reason !== undefined),
                  take(1)
                )
                .subscribe((reason: any) => {
                  if (reason === ModalCloseReason.PDP_LIST_VARIANTS_DISMISSED) {
                    this.setQueryParams.emit({ ks: null, a: null });
                    this.selectedAttributes$.next([]);
                  }
                })
            );
          }
        })
    );

    this.isUserLoggedIn$ = this.authService
      .isUserLoggedIn()
      .pipe(startWith(false), shareReplay({ bufferSize: 1, refCount: true }));

    this.activeCurrency$ = this.currencyService.getActive().pipe(
      map((isoCode: string) => {
        return getCurrencySymbol(isoCode, 'narrow');
      })
    );

    this.breakableFeeHint$ = this.isUserLoggedIn$.pipe(
      filter((isUserLoggedIn) => Boolean(isUserLoggedIn)),
      switchMap(() => this.translationService.translate('product.breakableFee_hint'))
    );

    this.loadingPrice$ = combineLatest([this.article$, this.quantityFormValueChanges$]).pipe(
      switchMap(([article, formValue]) => {
        if (!!article && !!formValue.quantity && !!formValue.unitCode) {
          const unit = article?.units?.find((u) => u.code === formValue.unitCode);
          return this.priceService.loadingPrice(article?.code, formValue.quantity, unit);
        } else {
          return of(false);
        }
      }),
      startWith(true),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const forceLoadPrice$ = merge(this.emitLoadPrice$.pipe(mapTo(true)), this.quantityFormValueChanges$.pipe(mapTo(false)));

    this.enableDisplayOfPrices$ = this.principalConfigurationService.isEnabled('enableDisplayOfPrices');

    this.articlePrice$ = combineLatest([this.article$, forceLoadPrice$, this.enableDisplayOfPrices$]).pipe(
      filter(([article, _forceLoadPrice, _enableDisplayOfPrices]) => {
        const formValue = this.quantityForm.value;
        return !!article && !!formValue.quantity && !!formValue.unitCode;
      }),
      switchMap(([article, forceLoadPrice, enableDisplayOfPrices]) => {
        const formValue = this.quantityForm.value;

        if (!article || !formValue.quantity || !formValue.unitCode || !enableDisplayOfPrices) {
          return of(null);
        }

        const unit = article?.units?.find((u) => u.code === formValue.unitCode);
        return this.priceService.getArticlePrice(article?.code, formValue.quantity, unit, false, forceLoadPrice);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  loadPrice(): void {
    this.emitLoadPrice$.next();
  }

  openModal(setAttributes = false): void {
    const modalData: PdpListVariantsModalData = {
      areaOfUseCode: this.areaOfUseCode$.value,
      product: this.product$.value,
    };

    if (setAttributes) {
      modalData.selectedAttributes = this.selectedAttributes$.value;
    }

    this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.PDP_LIST_VARIANTS, undefined, modalData);
  }

  updateSelectedAttribute(event: ArticlePickerEvent): void {
    if (this.invalid) {
      return;
    }
    const oldAttribute = this.selectedAttributes$.value.find((sa) => sa.attributeCode === event.attributeCode);

    if (!shallowEqualObjects(oldAttribute, event)) {
      let newAttributes = [...this.selectedAttributes$.value.filter((sa) => sa.attributeCode !== event.attributeCode)];

      if (!!event.value) {
        newAttributes.push(event);
      }
      if (!event.auto) {
        if (this.activatedRoute.snapshot.queryParamMap.has('a') && newAttributes.length > 1) {
          this.setQueryParams.emit({ ks: null, a: null });
        }
        newAttributes = newAttributes.filter((na) => !na.auto);
      }

      this.selectedAttributes$.next(newAttributes);
    }
  }

  ngOnDestroy(): void {
    this.selectedAttributes$.next([]);
    this.subscription.unsubscribe();
  }

  reset(): void {
    this.setQueryParams.emit({ ks: null, a: null });
    this.selectedAttributes$.next([]);
  }

  get shoppingListArticleNumber(): string {
    const articleNumber = this.article$?.value?.articleNumber;
    if (articleNumber === PrePrintedSpecial.VOLVO) {
      return this.prePrintedConfigurationControl?.value || articleNumber;
    }
    return articleNumber;
  }

  addToCart(): void {
    const article = this.article$.value;
    if (!!article) {
      const { quantity, unitCode } = this.quantityForm.value;
      const unit = article.units?.find((u) => u.code === unitCode);

      let customerMaterialNumber = article.articleNumber;
      if (article.articleNumber === PrePrintedSpecial.VOLVO) {
        customerMaterialNumber = this.prePrintedConfigurationControl?.value;
      }

      this.activeCartService.addEntry(
        article.code,
        quantity,
        unit,
        article.cartType,
        undefined,
        customerMaterialNumber,
        undefined,
        ItemListType.PDP_SKU_PICKER
      );
      this.showError = false;
    } else {
      this.showError = true;
    }
  }

  emitArticleOutOfStockChange(event: { status: boolean; outOfStockOnWebshop: boolean }): void {
    this.articleOutOfStockChange.emit(event);
    this.outOfStockOnWebshop$.next(event.outOfStockOnWebshop);
  }

  login(): void {
    this.router.navigateByUrl('/login');
  }
}
